// SmsButton — univerzálne SMS tlačidlo pre CELÝ Matrix (Mirko 21.7.).
// Použitie: <SmsButton to={telefon} entityType="dopyt" entityId={id} defaultText="..." onSent={...} />
// Ak SMS brána nie je nakonfigurovaná (čaká BulkGate token), tlačidlo je vypnuté s vysvetlením.
// Odoslanie sa zapíše do vlákna entity (dopyt/zákazka) — nervová sústava.
import React, { useEffect, useState } from 'react'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from './Toast.jsx'
import { normPhone } from '../utils/phone.js'
import { VyberCisla, UlozitKontakt } from './KontaktyPartnera.jsx'   // p107 — chrbtica: číslo z celej databázy, výzva uložiť kontakt

let _status = null // module cache: { configured, provider }
let _kontakty = null
export async function smsKontakty(force) { if (_kontakty && !force) return _kontakty; try { _kontakty = await fetchAuth('/api/sms/kontakty').then(r => r.json()) } catch (_) { _kontakty = [] } return _kontakty }
async function smsStatus() {
  if (_status) return _status
  try { _status = await fetchAuth('/api/sms/status').then(r => r.json()) } catch (_) { _status = { configured: false } }
  return _status
}
let _presety = null // module cache: [{id, nazov, text}] — editovateľné v Nastaveniach
export async function smsPresety(force) {
  if (_presety && !force) return _presety
  try { _presety = await fetchAuth('/api/sms/presety').then(r => r.json()) } catch (_) { _presety = [] }
  return _presety
}
// {stavba} {datum} {meno} {telefon} → doplní z kontextu (vars); nevyplnené ostanú viditeľné na ručnú úpravu
export const applyVars = (text, vars) => String(text || '').replace(/\{(\w+)\}/g, (m, k) => (vars && vars[k] != null && vars[k] !== '' ? String(vars[k]) : m))

// p103: startOpen + bezTlacidla + onClose = to isté okno otvorené z ponuky ⋮ (Kontakty) — žiadne druhé SMS okno (0.J)
export default function SmsButton({ to, entityType = '', entityId = '', defaultText = '', label = 'SMS', onSent, style, vars, startOpen = false, bezTlacidla = false, onClose }) {
  const toast = useToast()
  const [st, setSt] = useState(_status)
  const [open, setOpen] = useState(!!startOpen)
  const [ulozit, setUlozit] = useState(null)   // p107: { telefon } — SMS odišla na číslo, ktoré Matrix nepozná
  const zavri = () => { setOpen(false); setUlozit(null); onClose && onClose() }
  const [phone, setPhone] = useState(to || '')
  const [text, setText] = useState(defaultText || '')
  const [busy, setBusy] = useState(false)
  const [presety, setPresety] = useState([])
  const [kontakty, setKontakty] = useState([])
  useEffect(() => { smsStatus().then(setSt) }, [])
  useEffect(() => { if (open) smsKontakty().then(setKontakty) }, [open])
  useEffect(() => { setPhone(to || '') }, [to])
  useEffect(() => { if (open) smsPresety().then(setPresety) }, [open])

  const configured = !!(st && st.configured)
  const btnStyle = { padding: '6px 12px', border: '1px solid #d6dde6', background: '#fff', borderRadius: 8,
    cursor: configured ? 'pointer' : 'not-allowed', opacity: configured ? 1 : 0.5, fontWeight: 600,
    fontFamily: 'inherit', fontSize: 13, whiteSpace: 'nowrap', ...style }

  const send = async () => {
    if (busy) return
    if (!phone.trim() || !text.trim()) { toast.error('Vyplň číslo aj text.'); return }
    setBusy(true)
    try {
      const r = await fetchAuth('/api/sms/send', { method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: phone.trim(), text: text.trim(), entity_type: entityType, entity_id: entityId }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || !d.ok) throw new Error(d.error || 'odoslanie zlyhalo')
      toast.success('SMS odoslaná na ' + phone.trim())
      setText(defaultText || '')
      onSent && onSent()
      // p107 (Mirko 22. 9. 2026: „ak napíše niekto SMS niekomu — výzva na uloženie kontaktu"): neznáme číslo → ponúkni uloženie
      const n9 = phone.replace(/\D/g, '').slice(-9)
      const znamy = (await smsKontakty(true)).some(k => k.n === n9 && String(k.meno || '').trim())
      if (!znamy && n9.length === 9) setUlozit({ telefon: normPhone(phone.trim()) }); else zavri()
    } catch (e) { toast.error('SMS: ' + e.message) } finally { setBusy(false) }
  }

  // dĺžka: rovnaká transformácia ako brána (diakritika preč + ne-GSM interpunkcia na ASCII) — počítadlo sedí s realitou
  const plainLen = text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[\u201e\u201c\u201d\u2018\u2019\u201a]/g, "'").replace(/[\u2013\u2014]/g, '-').replace(/\u2026/g, '...').replace(/\u00b7/g, '-').length
  const segments = plainLen <= 160 ? 1 : Math.ceil(plainLen / 153)

  return (
    <>
      {!bezTlacidla && <button type="button" style={btnStyle} disabled={!configured}
        title={configured ? 'Poslať SMS' : 'SMS brána zatiaľ nie je zapojená (čaká sa na BulkGate token)'}
        onClick={() => configured && setOpen(true)}>{label}</button>}
      {open && (
        <div onClick={zavri} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, padding: 20, width: 'min(460px,94vw)' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ fontWeight: 800, fontSize: 15 }}>Poslať SMS</div>
              <button onClick={zavri} style={{ marginLeft: 'auto', padding: '5px 9px', border: 'none', background: '#f1f5f9', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
            </div>
            {ulozit ? (
              <div style={{ marginBottom: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#15803d', marginBottom: 6 }}>SMS odoslaná na {ulozit.telefon}.</div>
                <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 8 }}>Toto číslo Matrix nepozná. Ulož ho ku kontaktom — nabudúce ho nájdeš podľa mena v SMS, e-maile aj na každom doklade.</div>
                <UlozitKontakt telefon={ulozit.telefon} zrusText="Teraz nie" nadpis="ULOŽIŤ KONTAKT — ku ktorému partnerovi patrí?"
                  onUlozene={() => { smsKontakty(true); toast.success('Kontakt uložený'); zavri() }} onZrus={zavri} />
              </div>
            ) : (<>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>Komu
              <div style={{ marginTop: 4 }}>
                <VyberCisla value={phone} onChange={v => setPhone(/^[\d\s+()/-]+$/.test(v) ? normPhone(v) : v)} placeholder="Meno, firma alebo číslo…" />
              </div>
            </div>
            {presety.length > 0 && (
              <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginTop: 10 }}>Predloha
                <select value="" onChange={e => { const p = presety.find(x => String(x.id) === e.target.value); if (p) setText(applyVars(p.text, vars)) }}
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid #d6dde6', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box', marginTop: 4 }}>
                  <option value="">— vyber predlohu (texty upravíš v Nastaveniach) —</option>
                  {presety.map(p => <option key={p.id} value={p.id}>{p.nazov}</option>)}
                </select>
              </label>
            )}
            <label style={{ fontSize: 12, fontWeight: 700, color: '#475569', display: 'block', marginTop: 10 }}>Text
              <textarea value={text} onChange={e => setText(e.target.value)} rows={4} autoFocus
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d6dde6', borderRadius: 8, fontFamily: 'inherit', fontSize: 13, boxSizing: 'border-box', marginTop: 4, resize: 'vertical' }} />
            </label>
            <div style={{ fontSize: 11, color: plainLen > 160 ? '#b45309' : '#94a3b8', marginTop: 3 }}>
              {plainLen} znakov · {segments} SMS {plainLen > 160 ? '(dlhšia správa = viac SMS)' : ''} · diakritika sa odstráni
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
              <button onClick={zavri} style={{ padding: '8px 14px', border: '1px solid #d6dde6', background: '#fff', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 }}>Zrušiť</button>
              <button onClick={send} disabled={busy} style={{ padding: '8px 16px', border: 'none', background: '#16a34a', color: '#fff', borderRadius: 8, cursor: busy ? 'wait' : 'pointer', fontFamily: 'inherit', fontWeight: 700 }}>{busy ? 'Odosielam…' : 'Odoslať SMS'}</button>
            </div>
            </>)}
          </div>
        </div>
      )}
    </>
  )
}
